import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../context/RTLContext';
import { CardBox } from '../UI';
import { IconX } from '@tabler/icons-react';
import '../../styles/modern-auth.css';

const ENGINES = [
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'perplexity', label: 'Perplexity' },
];

const AuthModal = ({ isOpen, onClose, initialView = 'login' }) => {
  const [currentView, setCurrentView] = useState(initialView);
  const { t } = useTranslation();
  const { isRTL, direction } = useRTL();

  useEffect(() => {
    if (isOpen) setCurrentView(initialView);
  }, [isOpen, initialView]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Body scroll lock while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const handleSwitchToLogin = () => setCurrentView('login');
  const handleSwitchToRegister = () => setCurrentView('register');
  const handleAuthSuccess = () => onClose();

  if (!isOpen) return null;

  // Dot-pattern texture for the brand panel — subtle, low-contrast.
  const dotTexture = {
    backgroundImage: 'radial-gradient(rgba(20,30,15,0.10) 1px, transparent 1px)',
    backgroundSize: '24px 24px',
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
      dir={direction}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative w-full max-w-5xl mx-auto my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <CardBox className="overflow-hidden p-0 relative">
          {/* Close button — positions to outer corner regardless of LTR/RTL */}
          <button
            onClick={onClose}
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} z-10 p-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors`}
            aria-label={t('common.buttons.close', 'Close')}
          >
            <IconX size={20} className="text-gray-500 dark:text-gray-300" />
          </button>

          <div className="flex flex-col lg:flex-row">
            {/* Left — Form */}
            <div className="w-full lg:w-1/2 p-8 lg:p-12">
              <div className="mb-8">
                <img src="/brandon-logo.svg" alt="Brandon" className="h-10 w-auto" />
              </div>

              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 tracking-tight">
                  {currentView === 'login' ? t('auth.login.title') : t('auth.register.title')}
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  {currentView === 'login'
                    ? t('auth.login.subtitle')
                    : t('auth.register.subtitle')}
                </p>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentView}
                  initial={{ opacity: 0, x: isRTL ? -8 : 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: isRTL ? 8 : -8 }}
                  transition={{ duration: 0.18 }}
                >
                  {currentView === 'login' ? (
                    <LoginForm
                      onSwitchToRegister={handleSwitchToRegister}
                      onLoginSuccess={handleAuthSuccess}
                    />
                  ) : (
                    <RegisterForm
                      onSwitchToLogin={handleSwitchToLogin}
                      onRegisterSuccess={handleAuthSuccess}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right — Brand panel */}
            <div
              className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
              style={{ backgroundColor: '#d8f9b8' }}
            >
              {/* Texture layer */}
              <div className="absolute inset-0" style={dotTexture} />
              {/* Soft diagonal highlight */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 45%)',
                }}
              />

              {/* The brand panel is always lime regardless of theme, so every
                  text node uses literal-color arbitrary Tailwind values
                  (e.g. text-[#1a1a1a]) that bypass the global dark-mode
                  `.dark .text-gray-X { color: light }` cascade. */}
              <div className="relative z-10 flex flex-col justify-between h-full p-12 w-full">
                {/* Top label */}
                <div className="flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1a1a1a]" />
                  <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#1a1a1a]/80">
                    {t('auth.brandPanel.eyebrow', 'AEO Platform')}
                  </span>
                </div>

                {/* Hero statement */}
                <div className={`${isRTL ? 'text-right' : 'text-left'} max-w-md`}>
                  <motion.h2
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.08, ease: [0.2, 0.8, 0.2, 1] }}
                    className="text-[clamp(2.4rem,4.2vw,3.4rem)] leading-[0.95] font-bold text-[#1a1a1a] tracking-tight"
                  >
                    {t(
                      currentView === 'login'
                        ? 'auth.brandPanel.headlineLogin'
                        : 'auth.brandPanel.headlineRegister',
                      currentView === 'login'
                        ? 'See where you stand\nin AI search.'
                        : 'Track every answer.\nOptimize every engine.'
                    )
                      .split('\n')
                      .map((line, i) => (
                        <span key={i} className="block">{line}</span>
                      ))}
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
                    className="mt-6 text-sm text-[#1a1a1a]/85 leading-relaxed max-w-sm"
                  >
                    {t(
                      'auth.brandPanel.body',
                      'Brandon monitors how the three major AI engines answer questions about your category — and ranks the competitors mentioned alongside (or instead of) you.'
                    )}
                  </motion.p>
                </div>

                {/* Engine row */}
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#1a1a1a]/70 mb-3">
                    {t('auth.brandPanel.enginesLabel', 'Engines we track')}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ENGINES.map((engine, i) => (
                      <motion.span
                        key={engine.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.24 + i * 0.05 }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-900/90 text-white text-xs font-medium"
                      >
                        <span className="w-1 h-1 rounded-full bg-[#d8f9b8]" />
                        {engine.label}
                      </motion.span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardBox>
      </motion.div>
    </div>
  );
};

export default AuthModal;
